import React, { createContext, useContext, useState, useEffect } from 'react';
import { labelService } from '../services/labelService';
import toast from 'react-hot-toast';

const LabelContext = createContext();

export const useLabels = () => {
  const context = useContext(LabelContext);
  if (!context) {
    throw new Error('useLabels must be used within a LabelProvider');
  }
  return context;
};

export const LabelProvider = ({ children }) => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Load labels on mount
  useEffect(() => {
    loadLabels();
  }, []);

  // Listen for secret changes events to refresh labels
  useEffect(() => {
    const handleSecretChanged = () => {
      loadLabels();
    };

    window.addEventListener('secretChanged', handleSecretChanged);
    return () => {
      window.removeEventListener('secretChanged', handleSecretChanged);
    };
  }, []);

  const loadLabels = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await labelService.getLabels();
      setLabels(response.data || []);
    } catch (err) {
      setError(err);
      console.error('Failed to load labels:', err);
    } finally {
      setLoading(false);
    }
  };

  const createLabel = async (labelData) => {
    try {
      setCreating(true);
      const response = await labelService.createLabel(labelData);
      const newLabel = response.data;
      setLabels(prev => [...prev, newLabel]);
      toast.success('Label created successfully');
      return newLabel;
    } catch (err) {
      toast.error('Failed to create label');
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const updateLabel = async (id, labelData) => {
    try {
      setUpdating(true);
      const response = await labelService.updateLabel(id, labelData);
      const updatedLabel = response.data;
      setLabels(prev => prev.map(label => 
        label.id === id ? updatedLabel : label
      ));
      toast.success('Label updated successfully');
      return updatedLabel;
    } catch (err) {
      toast.error('Failed to update label');
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  const deleteLabel = async (id) => {
    try {
      setDeleting(true);
      await labelService.deleteLabel(id);
      setLabels(prev => prev.filter(label => label.id !== id));
      toast.success('Label deleted successfully');
    } catch (err) {
      toast.error('Failed to delete label');
      throw err;
    } finally {
      setDeleting(false);
    }
  };

  const refreshLabels = () => {
    loadLabels();
  };

  const value = {
    labels,
    loading,
    creating,
    updating,
    deleting,
    error,
    createLabel,
    updateLabel,
    deleteLabel,
    refreshLabels,
  };

  return (
    <LabelContext.Provider value={value}>
      {children}
    </LabelContext.Provider>
  );
};
